<script lang="ts">
  import { onDestroy, onMount, setContext } from "svelte";
  import { page } from "$app/stores";
  import { browser } from "$app/environment";
  import "$lib/styles/global.scss";
  import cfg from "$lib/config/config";
  import RegionSelectorModal from "$lib/components/RegionSelector/RegionSelectorModal/RegionSelectorModal.svelte";
  import { key, createRegionStore } from "$lib/stores/region";
  import { createSessionStore } from "$lib/stores/session";
  import { gotoRegion } from "$lib/components/RegionSelector";
  import GTM from "$lib/components/GTM/GTM.svelte";
  import Optimise from "$lib/components/GTM/Optimise.svelte";
  import Zendesk from "$lib/components/Zendesk/Zendesk.svelte";
  import Nosto from "$lib/components/Nosto/Nosto.svelte";
  import Voyado from "$lib/components/Voyado/Voyado.svelte";
  import NavHeader from "$lib/components/Nav/NavHeader/NavHeader.svelte";
  import { isLoading, _ } from "svelte-i18n";
  import Loader from "$lib/components/Loader/Loader.svelte";
  import { navTheme } from "$lib/stores/navTheme";
  import { PageTheme } from "$lib/gql/genCms/codegen";
  import PreviewControls from "$lib/components/PreviewControls/PreviewControls.svelte";
  import type { LayoutData } from "./$types";
  import PrivacyPolicyModal from "$lib/components/PrivacyPolicyModal/PrivacyPolicyModal.svelte";
  import SizeGuideModal from "$lib/components/SizeGuideModal/SizeGuideModal.svelte";
  import { modals } from "$lib/stores/modal";
  import Footer from "$lib/components/Footer/Footer.svelte";
  import { afterNavigate, beforeNavigate } from "$app/navigation";
  import { linkTo } from "$lib/utils/url";
  import { locale } from "svelte-i18n";
  import { get } from "svelte/store";
  import { cart } from "$lib/stores/cart";
  import AbTasty from "$lib/components/ABTasty/ABTasty.svelte";
  import { getCountry, getCountryRegion } from "$lib/data/regionSelector.data";
  import { nav as navStore } from "$lib/stores/nav";
  import { type Crumbs } from "$lib/components/Breadcrumbs/Breadcrumbs.svelte";
  import { mapPathToCrumb } from "$lib/utils/routes";
  import MemoryMonitorDisplay from "$lib/components/MemoryMonitor/MemoryMonitorDisplay.svelte";

  /** @type {import('./$types').PageData} */
  export let data: LayoutData;
  export let navData = data.navData;
  export let bannerData = data.bannerData;

  if (!$navStore) {
    navStore.set(navData);
  }

  let previousPathname: string;

  onMount(() => {
    if (window.location) previousPathname = window.location.pathname;
  });

  onMount(async () => {
    if (!$cart) {
      $cart = await cart.getCart();
    }
  });

  afterNavigate(() => {
    if (window.location.pathname !== previousPathname) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
      previousPathname = window.location.pathname;
    }
  });

  beforeNavigate(() => {
    if (window.location.pathname) {
      // Update the nav store with the new path
      navStore.previousCrumb(mapPathToCrumb(window.location.pathname));
    }
  });

  let isPrivacyPolicyOpen = modals.privacyPolicy;
  let isSizeGuideModalOpen = modals.sizeGuide;

  // initialise stores
  const session = createSessionStore(data.session);
  $: session.set(data.session);
  const userPrefs = data.session.userPrefs;
  const regionInfo = createRegionStore({
    locale: userPrefs.locale,
    country: userPrefs.country,
    region: cfg.REGION_ID === "staging" ? userPrefs.region : cfg.REGION_ID,
  });
  setContext(key, regionInfo);
  $: nav = navData?.data?.Nav;
  $: navHeaderTheme = $navTheme ? $navTheme : PageTheme.Dark;
  // Check if current page is a landing page and if there are no errors
  // To apply navTheme from CMS to landing pages only
  $: isLandingPage =
    ($page?.url.pathname === linkTo("/") ||
      `${$page?.url.pathname}${get(locale)?.split("-")[0]}` === linkTo("/") ||
      $page?.url.pathname === linkTo("/") + "/" ||
      $page?.url.pathname?.includes("/customer-service/gift-cards") ||
      $page?.url.pathname?.includes("/preview") ||
      $page?.url.pathname?.includes("/about-us") ||
      $page?.url.pathname?.includes("/account/circuit-master/login") ||
      ($page.params &&
        Object.prototype.hasOwnProperty.call($page.params, "landing") &&
        !Object.prototype.hasOwnProperty.call($page.params, "collection") &&
        $page?.error === null)) &&
    $page?.error === null;

  $: isPdpOrPlp =
    $page &&
    $page?.params &&
    (Object.prototype.hasOwnProperty.call($page?.params, "slug") ||
      Object.prototype.hasOwnProperty.call($page.params, "collection"));

  $: banners = bannerData?.data?.ListBanners?.edges.map(banner => {
    return {
      id: banner?.node?.id,
      createDate: banner?.node?.createDate,
      updateDate: banner?.node?.updateDate,
      publishFrom: banner?.node?.publishFrom,
      icon: banner?.node?.icon,
      text: banner?.node?.text,
      link1: banner?.node?.link1,
      link2: banner?.node?.link2,
      bgColour: banner?.node?.bgColour,
      textLight: banner?.node?.textLight,
    };
  });

  let showRegionModal = true;
  let countryCode: string | undefined;
  const cookiesEnabled = browser ? navigator.cookieEnabled : true;

  const unsubSession = session.subscribe(s => {
    regionInfo.init({
      locale: data.locale,
      country: s?.userPrefs?.country,
      region:
        cfg.REGION_ID === "staging" ? s?.userPrefs?.region : cfg.REGION_ID,
    });
    // show region modal if no region cookie set
    // or if the instance suggest header is present and doesn't match the current instance

    // check if the site region is different to the user's region

    if (cfg.REGION_ID === "staging") {
      showRegionModal = false;
    }

    if (
      s?.clientRegion &&
      getCountryRegion(s.clientRegion)?.regionCode === cfg.REGION_ID
    ) {
      showRegionModal = false;
    } else if (s.userPrefs.region === cfg.REGION_ID) {
      showRegionModal = false;
    } else if (s?.instanceSuggest && s?.instanceSuggest === cfg.REGION_ID) {
      showRegionModal = false;
    }

    countryCode = s.userPrefs.country || s.clientRegion;
  });

  onDestroy(unsubSession);

  // XXX: Necessary to specify script tag attributes in this slightly awkward
  // way in order to prevent `prettier` throwing a spurious error about
  // unrelated `:global` SCSS elsewhere in the component

  let dataEnvironment = "production";

  if (cfg.KLARNA_SUBDOMAIN) {
    cfg.KLARNA_SUBDOMAIN === "eu-library.playground"
      ? (dataEnvironment = "playground")
      : (dataEnvironment = "production");
  }

  const klarnaProps = {
    "data-environment": dataEnvironment,
    src: "https://js.klarna.com/web-sdk/v1/klarna.js",
    "data-client-id": cfg.KLARNA_CLIENT_ID,
  };
</script>

<style lang="scss">
  :global(.inline-anchor:hover) {
    text-decoration: none;
  }

  main {
    position: relative;
    background-color: $background;
    margin: 0 auto;
    box-sizing: border-box;
    z-index: 1;
    transform: translate3d(0, 0, 0); // to fix curtain reveal on safari
  }

  .skip {
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }

  .skip:focus {
    position: static;
    width: auto;
    height: auto;
  }
</style>

<svelte:head>
  {#if cfg.KLARNA_CLIENT_ID}
    <script async {...klarnaProps}></script>
  {/if}

  <script>
    (function (i, s, o, g, r, a, m) {
      i["RedealObject"] = r;
      (i[r] =
        i[r] ||
        function () {
          (i[r].q = i[r].q || []).push(arguments);
        }),
        (i[r].l = 1 * new Date());
      (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
      a.async = 1;
      a.src = g;
      m.parentNode.insertBefore(a, m);
    })(
      window,
      document,
      "script",
      "https://static.redeal.se/widget/js/redeal.js",
      "redeal",
    );
  </script>
  <script
    defer
    src="https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js"
  ></script>
  <script
    defer
    src={`https://st.pandect.es/${cfg.SHOPIFY_STORE}/pandectes-rules.js`}
  ></script>
  <script
    defer
    src={`https://gdpr-static.s3.us-east-1.amazonaws.com/c/pandectes-core.js?storeId=${
      cfg.NOSTO_ACCOUNT_ID.split("-")[1]
    }`}
  ></script>
  <!-- End Pandectes GDPR Compliance -->
</svelte:head>

<a href="#main" class="skip">{$_("skipBlockContent")}</a>
{#if !$page.url.pathname.includes("/preview/nav")}
  <AbTasty />
  <GTM gtmId={cfg.GTM_ID} nonce={$session.cspnonce} />
  <Optimise id={cfg.GTM_ID} nonce={$session.cspnonce} />
  <Zendesk key="2a9ea81a-30ea-4e6c-91b5-c143c5cb217a" />
  <Nosto />
  <Voyado />

  {#if $isLoading}
    <Loader />
  {:else}
    <NavHeader
      {nav}
      {banners}
      theme={navHeaderTheme}
      {isLandingPage}
      country={$regionInfo &&
        $regionInfo.country &&
        $regionInfo.country.toUpperCase()}
      language={$regionInfo &&
        $regionInfo.locale &&
        $regionInfo.locale.toUpperCase()}
      {regionInfo}
    />
    <main id="main">
      <slot />
    </main>
    <Footer {isPdpOrPlp} />

    {#if showRegionModal && !$page.url.pathname.includes("/account") && !$page.url.pathname.includes("/preview")}
      <RegionSelectorModal
        isOpen
        {countryCode}
        activeRegion={$session.instance}
        suggestedRegion={$session.instanceSuggest}
        preferences={$session.userPrefs}
        on:confirm={e => {
          gotoRegion(e.detail, regionInfo);
        }}
      />
    {/if}
  {/if}
{:else if $isLoading}
  <Loader />
{:else}
  <main>
    <slot />
  </main>
{/if}

<MemoryMonitorDisplay />

<PrivacyPolicyModal isOpen={$isPrivacyPolicyOpen} />
<SizeGuideModal isOpen={$isSizeGuideModalOpen} />

<PreviewControls preview={data?.preview} />
